import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Image as ImageIcon, AlertCircle, Plus } from 'lucide-react';
import { message } from 'antd';

const MultipleImageUpload = ({ 
    onUpload, 
    onRemove, 
    currentImages = [], 
    loading = false,
    accept = {
        'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxSize = 5 * 1024 * 1024, // 5MB
    maxFiles = 10, // Maximum number of images
    className = '',
    disabled = false,
    value, // Form field value
    onChange // Form field onChange
}) => {
    const [previews, setPreviews] = useState({});
    const [uploading, setUploading] = useState(false);

    const onDrop = useCallback(async (acceptedFiles) => {
        if (acceptedFiles.length === 0) return;

        // Use value from form field if available, otherwise use currentImages
        const images = value || currentImages;
        
        // Check if adding these files would exceed the limit
        const totalFiles = images.length + acceptedFiles.length;
        if (totalFiles > maxFiles) {
            message.error(`Maximum ${maxFiles} images allowed`);
            return;
        }

        setUploading(true);
        
        try {
            // Create previews for all files and add them to the form
            const newImageData = [];
            acceptedFiles.forEach((file) => {
                const preview = URL.createObjectURL(file);
                newImageData.push({
                    file: file,
                    preview: preview,
                    name: file.name
                });
            });

            // Update form field value
            const updatedImages = [...images, ...newImageData];
            console.log('Adding new images:', newImageData);
            console.log('Updated images array:', updatedImages);
            console.log('Form field onChange called with:', updatedImages);
            if (onChange) {
                onChange(updatedImages);
            }
            
            // Call onUpload with all files at once (for backward compatibility)
            if (onUpload) {
                await onUpload(newImageData);
            }
            
            message.success(`${acceptedFiles.length} image(s) added successfully!`);
        } catch (error) {
            console.error('Upload error:', error);
            message.error('Failed to add images');
        } finally {
            setUploading(false);
        }
    }, [onUpload, value, currentImages, maxFiles, onChange]);

    const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
        onDrop,
        accept,
        maxSize,
        multiple: true,
        disabled: disabled || loading || uploading
    });

    const handleRemove = (imageIndex) => {
        // Use value from form field if available, otherwise use currentImages
        const images = value || currentImages;
        const updatedImages = images.filter((_, i) => i !== imageIndex);
        
        console.log('Removing image at index:', imageIndex);
        console.log('Images before removal:', images);
        console.log('Images after removal:', updatedImages);
        
        // Update form field value
        if (onChange) {
            onChange(updatedImages);
        }
        
        // Call onRemove for backward compatibility
        if (onRemove) {
            onRemove(imageIndex);
        }
    };

    const fileRejectionErrors = fileRejections.map(({ file, errors }) => 
        errors.map(error => `${file.name}: ${error.message}`)
    ).flat();

    // Use value from form field if available, otherwise use currentImages
    const images = value || currentImages;
    const canAddMore = images.length < maxFiles;

    return (
        <div className={`multiple-image-upload ${className}`}>
            {/* Current Images Grid */}
            {images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
                    {images.map((image, index) => (
                        <div key={index} className="relative group">
                            <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                                <img
                                    src={image.preview || image.url || image.thumb || image}
                                    alt={`Menu image ${index + 1}`}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            {!disabled && !loading && !uploading && (
                                <button
                                    type="button"
                                    onClick={() => handleRemove(index)}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Upload Area */}
            {canAddMore && (
                <div
                    {...getRootProps()}
                    className={`
                        relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
                        transition-all duration-200 hover:border-blue-400 hover:bg-blue-50
                        ${isDragActive ? 'border-blue-500 bg-blue-100' : 'border-gray-300'}
                        ${disabled || loading || uploading ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                >
                    <input {...getInputProps()} />
                    
                    <div className="space-y-4">
                        <div className="flex justify-center">
                            {uploading ? (
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                            ) : (
                                <div className="p-3 bg-gray-100 rounded-full">
                                    <Plus size={24} className="text-gray-400" />
                                </div>
                            )}
                        </div>
                        
                        <div>
                            <p className="text-lg font-medium text-gray-700">
                                {isDragActive ? 'Drop images here' : 'Add more images'}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                                Drag & drop or click to select
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                                Supports: JPEG, PNG, GIF, WebP (max 5MB each)
                            </p>
                            <p className="text-xs text-gray-400">
                                {images.length}/{maxFiles} images uploaded
                            </p>
                        </div>
                        
                        {!isDragActive && (
                            <div className="flex justify-center">
                                <Upload size={20} className="text-gray-400" />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* File Rejection Errors */}
            {fileRejectionErrors.length > 0 && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start">
                        <AlertCircle size={16} className="text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                        <div>
                            <p className="text-sm font-medium text-red-800">Upload Error</p>
                            <ul className="text-sm text-red-700 mt-1">
                                {fileRejectionErrors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Status */}
            {(loading || uploading) && (
                <div className="mt-3 text-center">
                    <p className="text-sm text-blue-600">
                        {uploading ? 'Uploading images...' : 'Processing...'}
                    </p>
                </div>
            )}

            {/* Max Files Reached */}
            {!canAddMore && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                    <p className="text-sm text-yellow-800">
                        Maximum {maxFiles} images reached. Remove some images to add more.
                    </p>
                </div>
            )}
        </div>
    );
};

export default MultipleImageUpload;
